import { memo, type ReactNode } from 'react';
import { cn } from '../../../../lib/utils';
import { resolveAlign } from '../column-types';
import { readPlainText, resolveCellContent } from '../cell-content';
import { ALIGN_CLASS, DENSITY_CELL, type Density } from '../styles';
import type { DataGridColumn } from '../types';

/**
 * Memoized data row. Re-renders only when its own props change, so
 * expand/collapse and sort clicks don't trigger a full-grid re-render.
 *
 * Requires the caller to pass stable references for `columns` and
 * `onRowClick` (both are already wrapped in `useMemo`/`useCallback` in
 * every BStock + BStaff call site we ship today).
 */
export interface DataRowProps<T> {
  row: T;
  index: number;
  columns: DataGridColumn<T>[];
  density: Density;
  onRowClick?: (row: T, index: number) => void;
  /** Default currency code forwarded to `renderTypedCell` for money columns. */
  currency: string;
}

function DataRowImpl<T>({
  row,
  index,
  columns,
  density,
  onRowClick,
  currency,
}: DataRowProps<T>) {
  return (
    <tr
      onClick={onRowClick ? () => onRowClick(row, index) : undefined}
      className={cn(
        'border-b border-neutral-100 transition-colors',
        onRowClick && 'cursor-pointer hover:bg-neutral-50'
      )}
    >
      {columns.map((col) => {
        const align = resolveAlign(col);
        let content: ReactNode = resolveCellContent(col, row, currency);

        // When the column opts into ellipsis, wrap the content in a
        // div that truncates overflow and exposes the raw accessor
        // text as a native `title` tooltip. Using a block wrapper
        // (not the <td> itself) makes `max-width` actually honoured
        // under the default `table-layout: auto`.
        if (col.ellipsis) {
          content = (
            <div
              className="overflow-hidden text-ellipsis whitespace-nowrap"
              style={col.maxWidth ? { maxWidth: col.maxWidth } : undefined}
              title={readPlainText(col, row)}
            >
              {content}
            </div>
          );
        }

        return (
          <td
            key={col.id}
            className={cn(
              'align-middle text-dark-500',
              DENSITY_CELL[density],
              ALIGN_CLASS[align],
              col.cellClassName
            )}
          >
            {content}
          </td>
        );
      })}
    </tr>
  );
}

// `memo` loses the generic type parameter — we cast it back to
// preserve inference at call sites.
export const DataRow = memo(DataRowImpl) as <T>(
  props: DataRowProps<T>
) => ReactNode;
